import {
  AgendaTranscriptMappingMode,
  CallMedium,
  MinutesAnonymization,
  MinutesAttribution,
  MinutesDateFormat,
  MinutesLanguage,
  MinutesNameFormat,
  MinutesPassive,
  MinutesTimeFormat,
  MinutesVerboseness,
  MinutingAgent,
} from './calls.service'

/** The key source used to crypto-shred a meeting's artifacts. */
export type EncryptionKeySource = 'KMS'

/** Settings the client owns. */
export type ClientSettings = {
  allowSignUp: boolean
  minutesVerboseness: MinutesVerboseness
  minutesAnonymization: MinutesAnonymization
  minutesPassive: MinutesPassive
  minutesNameFormat: MinutesNameFormat
  minutesDateFormat: MinutesDateFormat
  minutesTimeFormat: MinutesTimeFormat
  minutesLanguage: MinutesLanguage
  minutesAttribution: MinutesAttribution
  anonymiseMemberSpeakers: boolean
  useTranscriptRefinement: boolean
  shareUrlOptOut: boolean
  transcriberInputLanguage: string
  transcriberOutputLanguage: string
  allowTranscriptDownload: boolean
  allowVideoDownload: boolean
  allowMinutesDownload: boolean
  allowOutlineDownload: boolean
  allowManualCallCreation: boolean
  allowCallUpload: boolean
  useVerificationDiscovery: boolean
  useAgendaTranscriptMapping: AgendaTranscriptMappingMode
  allowBoardPackUploads: boolean
  allowAdminCallActions: boolean
  allowRetentionExtensionRequests: boolean
  softDeletionPeriodInDays: number
  isIntercomEnabled: boolean
  isSharingEnabled: boolean
  isCalendarSynchronizationEnabled: boolean
  recordingType: 'VIDEO' | 'AUDIO'
  defaultCallMedium: CallMedium
  sendSoftDeletionWarningEmail: boolean
  softDeletionWarningHours: number
  sendSoftDeletionConfirmationEmail: boolean
  sendHardDeletionConfirmationEmail: boolean
  sendAssistantAccessDeniedEmail: boolean
  sendUpcomingCallReminderEmail: boolean
  sendWeeklyDigestEmail: boolean
  sendConsentNotification: boolean
  joinMeetingInternal: boolean
  joinMeetingExternal: boolean
  freezeJoinSettings: boolean
  useSectionHeadings: boolean
  usePartialMinutesRegeneration: boolean
  allowRestrictedUsers: boolean
  allowCallEntities: boolean
  allowActionItems: boolean
  allowLlmSpeechRecovery: boolean
  sessionTimeoutInMinutes: number
  dashboardUrl: string | null
  enabledMinutesLanguages: string[]
  minutingAgents: MinutingAgent[]
  useVirtualHostedStorageUrls: boolean
  contactEmails: string[]
  context: string[]
  assistantDisplayName: string | null
  llmModel: string | null
  encryptionKeySource: EncryptionKeySource
  retentionPeriodInDaysForMedia: number | null
  retentionPeriodInDaysForDocuments: number | null
  retentionPeriodInDaysForTranscript: number | null
  retentionPeriodInDaysForCall: number | null
  maxSingleRetentionExtensionInDays: number | null
  maxTotalRetentionExtensionInDays: number | null
  /** The contract the usage dashboard measures against; null until support records it. */
  subscriptionStartDate: string | null
  subscriptionAllowanceHours: number | null
  /** Percentages of the allowance at which the client's administrators are emailed. */
  usageAlertThresholdPercentages: number[]
}

/**
 * The effective configuration of an organization: its client's settings with the organization's
 * overrides applied, and the merged settings unioned.
 */
export type ResolvedSettings = ClientSettings

/** The organization-level overrides for settings whose null already means something on the client. */
export type OrganizationSettingOverrides = {
  assistantDisplayNameOverride: string | null
  llmModelOverride: string | null
  encryptionKeySourceOverride: EncryptionKeySource | null
  retentionPeriodInDaysForMediaOverride: number | null
  retentionPeriodInDaysForDocumentsOverride: number | null
  retentionPeriodInDaysForTranscriptOverride: number | null
  retentionPeriodInDaysForCallOverride: number | null
  maxSingleRetentionExtensionInDaysOverride: number | null
  maxTotalRetentionExtensionInDaysOverride: number | null
}

export const INHERITED_SETTING_NAMES = [
  'allowSignUp',
  'minutesVerboseness',
  'minutesAnonymization',
  'minutesPassive',
  'minutesNameFormat',
  'minutesDateFormat',
  'minutesTimeFormat',
  'minutesLanguage',
  'minutesAttribution',
  'anonymiseMemberSpeakers',
  'useTranscriptRefinement',
  'shareUrlOptOut',
  'transcriberInputLanguage',
  'transcriberOutputLanguage',
  'allowTranscriptDownload',
  'allowVideoDownload',
  'allowMinutesDownload',
  'allowOutlineDownload',
  'allowManualCallCreation',
  'allowCallUpload',
  'useVerificationDiscovery',
  'useAgendaTranscriptMapping',
  'allowBoardPackUploads',
  'allowAdminCallActions',
  'allowRetentionExtensionRequests',
  'softDeletionPeriodInDays',
  'isIntercomEnabled',
  'isSharingEnabled',
  'isCalendarSynchronizationEnabled',
  'recordingType',
  'defaultCallMedium',
  'sendSoftDeletionWarningEmail',
  'softDeletionWarningHours',
  'sendSoftDeletionConfirmationEmail',
  'sendHardDeletionConfirmationEmail',
  'sendAssistantAccessDeniedEmail',
  'sendUpcomingCallReminderEmail',
  'sendWeeklyDigestEmail',
  'sendConsentNotification',
  'joinMeetingInternal',
  'joinMeetingExternal',
  'freezeJoinSettings',
  'useSectionHeadings',
  'usePartialMinutesRegeneration',
  'allowRestrictedUsers',
  'allowCallEntities',
  'allowActionItems',
  'allowLlmSpeechRecovery',
  'sessionTimeoutInMinutes',
] as const

export const OVERRIDABLE_SETTING_NAMES = [
  'assistantDisplayName',
  'llmModel',
  'encryptionKeySource',
  'retentionPeriodInDaysForMedia',
  'retentionPeriodInDaysForDocuments',
  'retentionPeriodInDaysForTranscript',
  'retentionPeriodInDaysForCall',
  'maxSingleRetentionExtensionInDays',
  'maxTotalRetentionExtensionInDays',
] as const

export const CLIENT_ONLY_SETTING_NAMES = ['dashboardUrl', 'enabledMinutesLanguages', 'minutingAgents', 'useVirtualHostedStorageUrls'] as const

export const MERGED_SETTING_NAMES = ['contactEmails', 'context'] as const

export type InheritedSettingName = (typeof INHERITED_SETTING_NAMES)[number]
export type OverridableSettingName = (typeof OVERRIDABLE_SETTING_NAMES)[number]
export type MergedSettingName = (typeof MERGED_SETTING_NAMES)[number]

type ResolvableOrganization = {
  client: ClientSettings
} & Partial<Record<InheritedSettingName, unknown>> &
  Partial<Record<`${OverridableSettingName}Override`, unknown>> &
  Partial<Record<MergedSettingName, string[]>>

/**
 * Mirrors the backend's resolveSettings for the places that hold a raw organization rather than a
 * payload carrying `resolvedSettings`. Precedence is organization override, then client value, with
 * the merged settings unioned.
 */
export function resolveSettings(organization: ResolvableOrganization): ResolvedSettings {
  const resolved: Record<string, unknown> = { ...organization.client }
  for (const setting of INHERITED_SETTING_NAMES) {
    const override = organization[setting]
    if (override !== null && override !== undefined) {
      resolved[setting] = override
    }
  }
  for (const setting of OVERRIDABLE_SETTING_NAMES) {
    const override = organization[`${setting}Override`]
    if (override !== null && override !== undefined) {
      resolved[setting] = override
    }
  }
  for (const setting of MERGED_SETTING_NAMES) {
    resolved[setting] = [...new Set([...organization.client[setting], ...(organization[setting] ?? [])])]
  }
  return resolved as ResolvedSettings
}
